import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Plus,
  Linkedin,
  Instagram,
  Check,
  Clock,
  Edit2,
  Trash2,
  FileText,
  Globe,
  Sparkles,
  Loader2,
  RefreshCw,
  Star,
  Clapperboard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const STATUS_COLORS = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Draft' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Scheduled' },
  posted: { bg: 'bg-green-100', text: 'text-green-600', label: 'Posted' },
  completed: { bg: 'bg-[#0070FF]', text: 'text-white', label: 'Completed' },
};

const PLATFORM_CONFIG = {
  linkedin: { 
    icon: Linkedin, 
    color: '#0A66C2', 
    label: 'LinkedIn',
    audiences: ['sponsors', 'physicians']
  },
  meta: { 
    icon: Instagram, 
    color: '#E4405F', 
    label: 'Meta',
    audiences: ['patients']
  },
  blog: {
    icon: Globe,
    color: '#FF7F00',
    label: 'Blog',
    audiences: ['patients', 'sponsors', 'physicians']
  }
};

const ContentCalendar = ({ isOpen, onClose, contentHistory, onContentCreated, onSendToStudio }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month or week
  const [scheduledContent, setScheduledContent] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedScheduled, setSelectedScheduled] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });

  // Fetch scheduled content
  const fetchScheduledContent = useCallback(async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await axios.get(`${API}/calendar/scheduled`, {
        params: { month, year }
      });
      setScheduledContent(response.data);
    } catch (error) {
      console.error('Error fetching scheduled content:', error);
    }
  }, [currentDate]);

  useEffect(() => {
    if (isOpen) {
      fetchScheduledContent();
    }
  }, [isOpen, fetchScheduledContent]);

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }
    
    return days;
  };

  // Get content for a specific date
  const getContentForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return scheduledContent.filter(c => c.scheduled_date === dateStr);
  };

  // Get week days for weekly view
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push({
        day: day.getDate(),
        isCurrentMonth: day.getMonth() === currentDate.getMonth(),
        date: day,
        dayName: DAYS[day.getDay()]
      });
    }
    return days;
  };

  // Navigate weeks
  const prevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  // Auto-populate calendar
  const handleAutoPopulate = async () => {
    setIsAutoPopulating(true);
    try {
      const response = await axios.post(`${API}/calendar/auto-populate`, {
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        include_linkedin: true,
        include_meta: true,
        include_blog: true,
        include_significant_days: true
      });
      toast.success(`Created ${response.data.count} content items!`);
      fetchScheduledContent();
    } catch (error) {
      toast.error('Failed to auto-populate calendar');
    } finally {
      setIsAutoPopulating(false);
    }
  };

  // Clear auto-generated content
  const handleClearAutoGenerated = async () => {
    try {
      const response = await axios.delete(`${API}/calendar/auto-generated`, {
        params: { month: currentDate.getMonth() + 1, year: currentDate.getFullYear() }
      });
      toast.success(response.data.message);
      fetchScheduledContent();
    } catch (error) {
      toast.error('Failed to clear auto-generated items');
    }
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Handle date click
  const handleDateClick = (dayInfo) => {
    setSelectedDate(dayInfo.date);
    setShowScheduleModal(true);
  };

  // Handle scheduled content click
  const handleScheduledClick = (scheduled, e) => {
    e.stopPropagation();
    setSelectedScheduled(scheduled);
    setShowDetailModal(true);
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  if (!isOpen) return null;

  const days = getDaysInMonth(currentDate);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Calendar Modal */}
      <motion.div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        data-testid="content-calendar"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0070FF] to-[#FF7F00] p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CalendarIcon className="w-8 h-8" />
              <div>
                <h2 className="font-heading text-2xl font-bold">Content Calendar</h2>
                <p className="text-white/80 text-sm mt-1">
                  Schedule content for LinkedIn, Meta & Blog
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-populate button */}
              <button
                onClick={handleAutoPopulate}
                disabled={isAutoPopulating}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                title="Auto-fill M-F with content suggestions"
              >
                {isAutoPopulating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Auto-Fill Month
              </button>
              {/* View toggle */}
              <div className="flex bg-white/20 rounded-full p-1">
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    viewMode === 'month' ? 'bg-white text-[#0070FF]' : 'text-white/80 hover:text-white'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    viewMode === 'week' ? 'bg-white text-[#0070FF]' : 'text-white/80 hover:text-white'
                  }`}
                >
                  Week
                </button>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="calendar-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Month/Week Navigation */}
          <div className="flex items-center justify-center gap-6 mt-6">
            <button
              onClick={viewMode === 'month' ? prevMonth : prevWeek}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h3 className="font-heading text-xl font-bold min-w-[250px] text-center">
              {viewMode === 'month' 
                ? `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                : `Week of ${currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              }
            </h3>
            <button
              onClick={viewMode === 'month' ? nextMonth : nextWeek}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Linkedin className="w-4 h-4" style={{ color: '#0A66C2' }} />
            <span className="text-sm text-gray-600">LinkedIn</span>
          </div>
          <div className="flex items-center gap-2">
            <Instagram className="w-4 h-4" style={{ color: '#E4405F' }} />
            <span className="text-sm text-gray-600">Meta</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" style={{ color: '#FF7F00' }} />
            <span className="text-sm text-gray-600">Blog (Fridays)</span>
          </div>
          <div className="flex-1" />
          {Object.entries(STATUS_COLORS).map(([key, value]) => (
            <div key={key} className="flex items-center gap-1">
              <span className={`w-3 h-3 rounded-full ${value.bg}`} />
              <span className="text-xs text-gray-500">{value.label}</span>
            </div>
          ))}
          <button
            onClick={handleClearAutoGenerated}
            className="text-xs text-red-500 hover:text-red-700 ml-4"
          >
            Clear Auto-Generated
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-6">
          {viewMode === 'month' ? (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {DAYS.map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Days grid - Month View */}
              <div className="grid grid-cols-7 gap-2">
                {days.map((dayInfo, index) => {
                  const dayContent = getContentForDate(dayInfo.date);
                  const hasContent = dayContent.length > 0;
                  
                  return (
                    <motion.div
                      key={index}
                      className={`min-h-[100px] p-2 rounded-xl border transition-all cursor-pointer ${
                        dayInfo.isCurrentMonth 
                          ? 'bg-white border-gray-200 hover:border-[#0070FF] hover:shadow-md' 
                          : 'bg-gray-50 border-gray-100 opacity-50'
                      } ${isToday(dayInfo.date) ? 'ring-2 ring-[#FF7F00]' : ''}`}
                      onClick={() => dayInfo.isCurrentMonth && handleDateClick(dayInfo)}
                      whileHover={{ scale: dayInfo.isCurrentMonth ? 1.02 : 1 }}
                      data-testid={`calendar-day-${dayInfo.day}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${
                          isToday(dayInfo.date) ? 'text-[#0070FF] font-bold' : 'text-gray-700'
                        }`}>
                          {dayInfo.day}
                        </span>
                        {dayInfo.isCurrentMonth && (
                          <Plus className="w-4 h-4 text-gray-300 hover:text-[#0070FF] transition-colors" />
                        )}
                      </div>
                      
                      {/* Scheduled content for this day */}
                      <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                        {dayContent.slice(0, 3).map((content, i) => {
                          const PlatformIcon = PLATFORM_CONFIG[content.platform]?.icon || FileText;
                          const statusStyle = STATUS_COLORS[content.status] || STATUS_COLORS.draft;
                          
                          return (
                            <motion.div
                              key={content.id}
                              className={`p-1.5 rounded-md text-xs truncate flex items-center gap-1 cursor-pointer ${statusStyle.bg} ${statusStyle.text} hover:ring-2 hover:ring-black/20`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleScheduledClick(content, e);
                              }}
                              whileHover={{ scale: 1.02 }}
                              data-testid={`scheduled-item-${content.id}`}
                            >
                              <PlatformIcon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{content.title}</span>
                              {content.significant_day && <Star className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
                            </motion.div>
                          );
                        })}
                        {dayContent.length > 3 && (
                          <span className="text-xs text-gray-400">+{dayContent.length - 3} more</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          ) : (
            /* Week View */
            <div className="space-y-4">
              {/* Week day headers */}
              <div className="grid grid-cols-7 gap-3">
                {getWeekDays().map((dayInfo, index) => (
                  <div 
                    key={index}
                    className={`text-center p-2 rounded-lg ${
                      isToday(dayInfo.date) ? 'bg-[#0070FF] text-white' : 'bg-gray-100'
                    }`}
                  >
                    <div className="text-xs font-medium">{dayInfo.dayName}</div>
                    <div className="text-lg font-bold">{dayInfo.day}</div>
                  </div>
                ))}
              </div>

              {/* Week content grid */}
              <div className="grid grid-cols-7 gap-3">
                {getWeekDays().map((dayInfo, index) => {
                  const dayContent = getContentForDate(dayInfo.date);
                  const isWeekend = dayInfo.date.getDay() === 0 || dayInfo.date.getDay() === 6;
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[300px] p-3 rounded-xl border transition-all ${
                        isWeekend ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-[#0070FF]'
                      } ${isToday(dayInfo.date) ? 'ring-2 ring-[#FF7F00]' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-gray-500">
                          {dayContent.length} items
                        </span>
                        <button
                          onClick={() => handleDateClick(dayInfo)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <Plus className="w-4 h-4 text-gray-400 hover:text-[#0070FF]" />
                        </button>
                      </div>
                      
                      <ScrollArea className="h-[250px]">
                        <div className="space-y-2 pr-2">
                          {dayContent.map((content, i) => {
                            const PlatformIcon = PLATFORM_CONFIG[content.platform]?.icon || FileText;
                            const platformColor = PLATFORM_CONFIG[content.platform]?.color || '#666';
                            const statusStyle = STATUS_COLORS[content.status] || STATUS_COLORS.draft;
                            
                            return (
                              <motion.div
                                key={content.id}
                                className="p-2 rounded-lg border border-gray-100 bg-white hover:shadow-md transition-shadow cursor-pointer"
                                onClick={(e) => handleScheduledClick(content, e)}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                              >
                                <div className="flex items-start gap-2">
                                  <div 
                                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: `${platformColor}20` }}
                                  >
                                    <PlatformIcon className="w-3 h-3" style={{ color: platformColor }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium line-clamp-2">{content.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${statusStyle.bg} ${statusStyle.text}`}>
                                        {statusStyle.label}
                                      </span>
                                      {content.scheduled_time && (
                                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                          <Clock className="w-2.5 h-2.5" />
                                          {content.scheduled_time}
                                        </span>
                                      )}
                                      {content.significant_day && (
                                        <Star className="w-3 h-3 text-yellow-500" title={content.significant_day} />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                          {dayContent.length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-xs">
                              {isWeekend ? 'Weekend' : 'No content scheduled'}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Schedule Modal */}
      <AnimatePresence>
        {showScheduleModal && selectedDate && (
          <ScheduleModal
            date={selectedDate}
            contentHistory={contentHistory}
            onClose={() => {
              setShowScheduleModal(false);
              setSelectedDate(null);
            }}
            onSchedule={async (data) => {
              try {
                await axios.post(`${API}/calendar/schedule`, data);
                toast.success('Content scheduled!');
                fetchScheduledContent();
                setShowScheduleModal(false);
                setSelectedDate(null);
              } catch (error) {
                toast.error('Failed to schedule content');
              }
            }}
            onCreateNew={onContentCreated}
          />
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedScheduled && (
          <DetailModal
            scheduled={selectedScheduled}
            contentHistory={contentHistory}
            onSendToStudio={onSendToStudio}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedScheduled(null);
            }}
            onUpdate={async (id, data) => {
              try {
                await axios.put(`${API}/calendar/scheduled/${id}`, data);
                toast.success('Updated!');
                fetchScheduledContent();
              } catch (error) {
                toast.error('Failed to update');
              }
            }}
            onDelete={async (id) => {
              try {
                await axios.delete(`${API}/calendar/scheduled/${id}`);
                toast.success('Removed from calendar');
                fetchScheduledContent();
                setShowDetailModal(false);
                setSelectedScheduled(null);
              } catch (error) {
                toast.error('Failed to delete');
              }
            }}
            onGenerateContent={async (scheduled) => {
              try {
                const response = await axios.post(`${API}/calendar/generate-content`, {
                  scheduled_id: scheduled.id,
                  title: scheduled.title,
                  audience: scheduled.audience,
                  platform: scheduled.platform,
                  significant_day: scheduled.significant_day
                });
                toast.success('Viral content generated & saved!');
                fetchScheduledContent();
                return response.data;
              } catch (error) {
                toast.error('Failed to generate content');
                return null;
              }
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Schedule Modal Component
const ScheduleModal = ({ date, contentHistory, onClose, onSchedule, onCreateNew }) => {
  const [mode, setMode] = useState('existing'); // existing or new
  const [selectedContent, setSelectedContent] = useState(null);
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState('linkedin');
  const [audience, setAudience] = useState('sponsors');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');

  const dateStr = date.toISOString().split('T')[0];
  const formattedDate = date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  // Filter content based on selected platform
  const filteredContent = contentHistory.filter(c => {
    const platformConfig = PLATFORM_CONFIG[platform];
    return platformConfig?.audiences.includes(c.audience);
  });

  const handleSchedule = () => {
    if (mode === 'existing' && !selectedContent) {
      toast.error('Please select content to schedule');
      return;
    }
    if (mode === 'new' && !title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    const data = {
      content_id: mode === 'existing' ? selectedContent.id : null,
      title: mode === 'existing' ? selectedContent.title : title,
      content_preview: mode === 'existing' ? selectedContent.content?.substring(0, 200) : null,
      audience: mode === 'existing' ? selectedContent.audience : audience,
      platform,
      scheduled_date: dateStr,
      scheduled_time: time || null,
      status: 'scheduled',
      notes: notes || null,
    };

    onSchedule(data);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      
      <motion.div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div className="bg-black text-white p-4">
          <h3 className="font-heading font-bold">Schedule for {formattedDate}</h3>
        </div>

        <div className="p-6 space-y-4">
          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
            <div className="flex gap-2">
              {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setPlatform(key);
                      setSelectedContent(null);
                      setAudience(config.audiences[0]);
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      platform === key 
                        ? 'border-black bg-black text-white' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{config.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {platform === 'linkedin' ? 'For Sponsors & Physicians' : platform === 'blog' ? 'For Website Blog' : 'For Patients'}
            </p>
          </div>

          {/* Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Source</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMode('existing')}
                className={`flex-1 p-2 rounded-lg border transition-all text-sm ${
                  mode === 'existing' ? 'border-[#0070FF] bg-[#0070FF]/20' : 'border-gray-200'
                }`}
              >
                From My Content
              </button>
              <button
                onClick={() => setMode('new')}
                className={`flex-1 p-2 rounded-lg border transition-all text-sm ${
                  mode === 'new' ? 'border-[#0070FF] bg-[#0070FF]/20' : 'border-gray-200'
                }`}
              >
                New Entry
              </button>
            </div>
          </div>

          {mode === 'existing' ? (
            /* Select from existing content */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Content ({filteredContent.length} available)
              </label>
              <ScrollArea className="h-[200px] border border-gray-200 rounded-xl">
                <div className="p-2 space-y-2">
                  {filteredContent.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No content available for {PLATFORM_CONFIG[platform]?.label}
                      <button
                        onClick={() => setMode('new')}
                        className="block mx-auto mt-2 text-[#0070FF] font-medium"
                      >
                        Create new entry instead
                      </button>
                    </div>
                  ) : (
                    filteredContent.map(content => (
                      <button
                        key={content.id}
                        onClick={() => setSelectedContent(content)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedContent?.id === content.id
                            ? 'border-[#0070FF] bg-[#0070FF]/10'
                            : 'border-gray-100 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-sm line-clamp-1">{content.title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {content.audience} • {content.format_type}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            /* New entry form */
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter content title..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#7000FF] focus:ring-1 focus:ring-[#7000FF] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Audience</label>
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#7000FF] focus:ring-1 focus:ring-[#7000FF] outline-none"
                >
                  {PLATFORM_CONFIG[platform]?.audiences.map(a => (
                    <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Time (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time (optional)
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#7000FF] focus:ring-1 focus:ring-[#7000FF] outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#7000FF] focus:ring-1 focus:ring-[#7000FF] outline-none resize-none"
            />
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            className="flex-1 px-4 py-2 bg-black text-white rounded-full font-medium hover:bg-[#7000FF] transition-colors"
            data-testid="schedule-confirm-btn"
          >
            Schedule
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Detail Modal Component
const DetailModal = ({ scheduled, onClose, onUpdate, onDelete, contentHistory, onGenerateContent, onSendToStudio }) => {
  const [status, setStatus] = useState(scheduled.status);
  const [notes, setNotes] = useState(scheduled.notes || '');
  const [isEditing, setIsEditing] = useState(false);
  const [showContentPicker, setShowContentPicker] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);

  const PlatformIcon = PLATFORM_CONFIG[scheduled.platform]?.icon || FileText;
  const statusStyle = STATUS_COLORS[status] || STATUS_COLORS.draft;

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    onUpdate(scheduled.id, { status: newStatus });
  };

  const handleSaveNotes = () => {
    onUpdate(scheduled.id, { notes });
    setIsEditing(false);
  };

  // Resolve full script for "Send to Studio" (Meta only)
  const linkedContent = scheduled.content_id
    ? (contentHistory || []).find((c) => c.id === scheduled.content_id)
    : null;
  const reelScript = (generatedContent?.content) || (linkedContent?.content) || scheduled.content_preview || '';
  const canSendToStudio = scheduled.platform === 'meta' && !!onSendToStudio && reelScript.trim().length > 0;

  const handleReplaceContent = (content) => {
    onUpdate(scheduled.id, { 
      content_id: content.id,
      title: content.title,
      content_preview: content.content?.substring(0, 200) 
    });
    setShowContentPicker(false);
  };

  const handleGenerateContent = async () => {
    setIsGenerating(true);
    try {
      const result = await onGenerateContent(scheduled);
      if (result) {
        setGeneratedContent(result);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Quick status cycle: draft -> scheduled -> posted
  const cycleStatus = () => {
    const statusOrder = ['draft', 'scheduled', 'posted', 'completed'];
    const currentIndex = statusOrder.indexOf(status);
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    handleStatusChange(statusOrder[nextIndex]);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      
      <motion.div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div 
          className="p-4"
          style={{ 
            background: `linear-gradient(135deg, ${PLATFORM_CONFIG[scheduled.platform]?.color || '#0070FF'}, #1A1A2E)` 
          }}
        >
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <PlatformIcon className="w-5 h-5" />
              <span className="font-medium">{PLATFORM_CONFIG[scheduled.platform]?.label}</span>
              {scheduled.significant_day && (
                <span className="px-2 py-0.5 bg-yellow-500 text-black text-xs rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  {scheduled.significant_day}
                </span>
              )}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <h3 className="font-heading font-bold text-lg">{scheduled.title}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {new Date(scheduled.scheduled_date).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
              {scheduled.scheduled_time && ` at ${scheduled.scheduled_time}`}
            </p>
            {scheduled.is_auto_generated && (
              <span className="inline-block mt-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                Auto-generated
              </span>
            )}
          </div>

          {scheduled.content_preview && !generatedContent && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700 line-clamp-3">{scheduled.content_preview}</p>
            </div>
          )}

          {/* Generate Content Button */}
          {!scheduled.content_id && !generatedContent && (
            <button
              onClick={handleGenerateContent}
              disabled={isGenerating}
              className="w-full p-4 bg-gradient-to-r from-[#0070FF] to-[#FF7F00] text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating Viral Content...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Viral Content/Script
                </>
              )}
            </button>
          )}

          {/* Generated Content Display */}
          {generatedContent && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Content Generated & Saved!
                </span>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 max-h-[300px] overflow-y-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{generatedContent.content}</pre>
              </div>
            </div>
          )}

          {/* Send to Video Studio (Meta Reels only) */}
          {canSendToStudio && (
            <button
              onClick={() => {
                onSendToStudio({ title: scheduled.title, script: reelScript });
                onClose();
              }}
              className="w-full p-3 bg-gradient-to-r from-[#FF7F00] to-[#FF9933] text-black rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all"
              data-testid="send-to-studio-btn"
            >
              <Clapperboard className="w-4 h-4" />
              Generate Reel from this script
            </button>
          )}

          {/* Quick Status Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                {statusStyle.label}
              </span>
            </div>
            <button
              onClick={cycleStatus}
              className="px-3 py-1 bg-[#0070FF] text-white text-sm rounded-lg hover:bg-[#0055CC] transition-colors flex items-center gap-1"
            >
              <Check className="w-4 h-4" />
              Mark as {status === 'draft' ? 'Scheduled' : status === 'scheduled' ? 'Posted' : status === 'posted' ? 'Completed' : 'Draft'}
            </button>
          </div>

          {/* Status Selection Grid */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Change Status</label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(STATUS_COLORS).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => handleStatusChange(key)}
                  className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1 ${
                    status === key 
                      ? `border-[#0070FF] ${value.bg}` 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {status === key && <Check className="w-3 h-3" />}
                  <span className="text-xs font-medium">{value.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Replace Content */}
          {contentHistory && contentHistory.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Replace with My Content</label>
              {showContentPicker ? (
                <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {contentHistory.map((content) => (
                    <button
                      key={content.id}
                      onClick={() => handleReplaceContent(content)}
                      className="w-full p-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="text-sm font-medium truncate">{content.title}</p>
                      <p className="text-xs text-gray-500">{content.audience} • {content.content_type}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <button
                  onClick={() => setShowContentPicker(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#0070FF] hover:text-[#0070FF] transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Replace with saved content
                </button>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Notes</label>
              {!isEditing && (
                <button 
                  onClick={() => setIsEditing(true)}
                  className="text-xs text-[#0070FF] hover:underline"
                >
                  Edit
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#0070FF] focus:ring-1 focus:ring-[#0070FF] outline-none resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    className="px-3 py-1 text-sm bg-[#0070FF] text-white rounded"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg min-h-[60px]">
                {notes || 'No notes added'}
              </p>
            )}
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => onDelete(scheduled.id)}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-full transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#0070FF] text-white rounded-full font-medium hover:bg-[#FF7F00] transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContentCalendar;
